const COMMON_EMAIL_DOMAINS = [
    "gmail.com",
    "hotmail.com",
    "outlook.com",
    "icloud.com",
    "yahoo.com",
    "live.com",
    "me.com",
];

const SUGGESTION_DISTANCE_THRESHOLD = 2;

/**
 * Extracts the domain from an email address.
 *
 * @param email - The raw email string entered by the user.
 * @returns The domain portion after the '@' symbol, or null when not present.
 */
const getEmailDomain = (email: string): string | null => {
    const normalizedEmail = email.trim().toLowerCase();
    const atIndex = normalizedEmail.lastIndexOf("@");

    if (atIndex <= 0 || atIndex === normalizedEmail.length - 1) {
        return null;
    }

    const domain = normalizedEmail.slice(atIndex + 1);
    if (!domain || domain.includes(" ")) {
        return null;
    }

    return domain;
};

/**
 * Computes the Levenshtein distance between two strings.
 *
 * This distance is the minimum number of single-character edits required to
 * transform one string into the other.
 *
 * @param a - First string.
 * @param b - Second string.
 * @returns The Levenshtein distance.
 */
const levenshteinDistance = (a: string, b: string): number => {
    if (a === b) {
        return 0;
    }

    const aLength = a.length;
    const bLength = b.length;

    if (aLength === 0) {
        return bLength;
    }

    if (bLength === 0) {
        return aLength;
    }

    const previousRow = new Array(bLength + 1);
    const currentRow = new Array(bLength + 1);

    for (let j = 0; j <= bLength; j++) {
        previousRow[j] = j;
    }

    for (let i = 1; i <= aLength; i++) {
        currentRow[0] = i;
        const aChar = a[i - 1];

        for (let j = 1; j <= bLength; j++) {
            const substitutionCost = aChar === b[j - 1] ? 0 : 1;
            currentRow[j] = Math.min(
                previousRow[j] + 1,
                currentRow[j - 1] + 1,
                previousRow[j - 1] + substitutionCost
            );
        }

        for (let j = 0; j <= bLength; j++) {
            previousRow[j] = currentRow[j];
        }
    }

    return previousRow[bLength] as number;
};

/**
 * Returns a suggested corrected email address for common mistyped domains.
 *
 * The suggestion is only returned when the domain part of the email is
 * close to a known common provider and the computed Levenshtein distance
 * is within the defined threshold.
 *
 * @param email - The full email address entered by the user.
 * @returns A corrected email suggestion, or null when no suggestion is available.
 */
export const getSuggestedEmailCorrection = (email: string): string | null => {
    const domain = getEmailDomain(email);
    if (!domain || COMMON_EMAIL_DOMAINS.includes(domain)) {
        return null;
    }

    let closestDomain: string | null = null;
    let closestDistance = Infinity;

    for (const candidate of COMMON_EMAIL_DOMAINS) {
        const distance = levenshteinDistance(domain, candidate);
        if (distance < closestDistance) {
            closestDomain = candidate;
            closestDistance = distance;
        }
    }

    if (closestDomain && closestDistance <= SUGGESTION_DISTANCE_THRESHOLD) {
        const atIndex = email.lastIndexOf("@");
        return `${email.slice(0, atIndex + 1)}${closestDomain}`;
    }

    return null;
};
